/**
 * Subscription TanStack Query hooks.
 */

import { useQuery } from "@tanstack/react-query";
import { subscriptionKeys } from "@/queries/keys";
import * as paymentApi from "./api";
import type { PaymentSourceType, UserSubscription } from "./types";
import {
  SubscriptionPlanId,
  SubscriptionStatus,
  OfferIds,
  PaymentSourceType as PST,
} from "./types";

const DEV_MOCK_SUBSCRIPTIONS: Record<string, UserSubscription> | null =
  import.meta.env.DEV
    ? {
        pro: {
          planId: SubscriptionPlanId.Speak_Custom,
          offerId: OfferIds.Speak_Pro,
          paymentSource: PST.Stripe,
          status: SubscriptionStatus.Active,
          isFree: false,
          isExpired: false,
          isEnterprise: false,
          isPricing: true,
          isPaymentMethod: true,
          name: "Pro",
          durationId: "monthly",
          currency: "usd",
          startDate: "2026-01-01T00:00:00Z",
          billStartDate: "2026-04-01T00:00:00Z",
          billEndDate: "2026-05-01T00:00:00Z",
          remainingDays: 29,
          remainingSeconds: 2505600,
          freeMinutes: 1500,
          usedMinutes: 320,
          totalUsedMinutes: 320,
          freeNotes: 100,
          freeCharacters: 500000,
          totalMedia: 42,
          referralId: "",
          invoiceLink: "",
          notes: { totalNotes: 100, remainingNotes: 80 },
          cost: {
            insights: 0,
            prompts: 2,
            humanTranscription: 0,
            monthly: 15,
            texts: 0,
            total: 15,
            discountedTotal: 15,
            dubbing: 0,
            translation: 0,
          },
          features: {
            collaboration: {
              adminControl: true,
              allowUsers: 5,
              assignStatus: true,
              groupTeamMember: true,
              teamStatistics: true,
            },
            embed: {
              analytics: true,
              brandCustomization: true,
              globalBrandCustomization: false,
              isFooter: false,
              library: true,
              player: true,
              watermark: false,
              isPlayerDomain: false,
              isLibraryDomain: false,
            },
            developers: { apiKeys: true, webhooks: true },
            export: {
              bulk: true,
              premium: true,
              csv: true,
              html: true,
              json: true,
              pdf: true,
              redaction: false,
              vtt: true,
              word: true,
            },
            import: { bulk: true, csv: true, fileSizeInGB: 10 },
            insights: { edit: true, custom: true },
            texts: { csv: 100, notes: 100 },
            recorder: {
              brandCustomization: true,
              download: true,
              isFooter: false,
              isRecorderDomain: false,
            },
            support: { liveChat: true, phone: false, training: false },
            transcript: { edit: true, totalMinutes: 1500 },
            translation: { dubbingMinutes: 0, totalCharacters: 0 },
            prompt: {
              isEmbeddings: true,
              totalCharacters: 500000,
              folderChat: true,
              mediaChat: true,
            },
            whiteLabel: { hosting: false },
            meetingAssistant: { isActive: true, customization: true },
            delete: { isCustomize: false, mediaInDays: -1 },
            liveTranscription: { isEnabled: true, reprocessFile: true },
            liveTranslation: { isEnabled: false },
          },
        },
        payg: {
          planId: SubscriptionPlanId.Speak_Pay_As_You_Go,
          offerId: OfferIds.Speak_Starter,
          paymentSource: PST.Stripe,
          status: SubscriptionStatus.Active,
          isFree: false,
          isExpired: false,
          isEnterprise: false,
          isPricing: true,
          isPaymentMethod: true,
          name: "Pay As You Go",
          durationId: "monthly",
          currency: "usd",
          startDate: "2026-01-01T00:00:00Z",
          billStartDate: "2026-04-01T00:00:00Z",
          billEndDate: "2026-05-01T00:00:00Z",
          remainingDays: 29,
          remainingSeconds: 2505600,
          freeMinutes: 0,
          usedMinutes: 45,
          totalUsedMinutes: 45,
          freeNotes: 0,
          freeCharacters: 0,
          totalMedia: 12,
          referralId: "",
          invoiceLink: "",
          notes: { totalNotes: 0, remainingNotes: 0 },
          cost: {
            insights: 0,
            prompts: 0,
            humanTranscription: 0,
            monthly: 0,
            texts: 0,
            total: 28,
            discountedTotal: 28,
            dubbing: 0,
            translation: 0,
          },
          features: {
            collaboration: {
              adminControl: false,
              allowUsers: 1,
              assignStatus: false,
              groupTeamMember: false,
              teamStatistics: false,
            },
            embed: {
              analytics: false,
              brandCustomization: false,
              globalBrandCustomization: false,
              isFooter: true,
              library: false,
              player: true,
              watermark: true,
              isPlayerDomain: false,
              isLibraryDomain: false,
            },
            developers: { apiKeys: true, webhooks: true },
            export: {
              bulk: false,
              premium: false,
              csv: true,
              html: true,
              json: true,
              pdf: false,
              redaction: false,
              vtt: true,
              word: false,
            },
            import: { bulk: false, csv: false, fileSizeInGB: 2 },
            insights: { edit: false, custom: false },
            texts: { csv: 0, notes: 0 },
            recorder: {
              brandCustomization: false,
              download: true,
              isFooter: true,
              isRecorderDomain: false,
            },
            support: { liveChat: false, phone: false, training: false },
            transcript: { edit: true, totalMinutes: 0 },
            translation: { dubbingMinutes: 0, totalCharacters: 0 },
            prompt: {
              isEmbeddings: false,
              totalCharacters: 0,
              folderChat: false,
              mediaChat: false,
            },
            whiteLabel: { hosting: false },
            meetingAssistant: { isActive: true, customization: false },
            delete: { isCustomize: false, mediaInDays: 1 },
            liveTranscription: { isEnabled: false, reprocessFile: false },
            liveTranslation: { isEnabled: false },
          },
        },
        cancel: {
          planId: SubscriptionPlanId.Speak_Custom,
          offerId: OfferIds.Speak_Pro,
          paymentSource: PST.Stripe,
          status: SubscriptionStatus.PendingCancellation,
          isFree: false,
          isExpired: false,
          isEnterprise: false,
          isPricing: true,
          isPaymentMethod: true,
          name: "Pro",
          durationId: "monthly",
          currency: "usd",
          startDate: "2026-01-01T00:00:00Z",
          billStartDate: "2026-04-01T00:00:00Z",
          billEndDate: "2026-05-01T00:00:00Z",
          remainingDays: 29,
          remainingSeconds: 2505600,
          freeMinutes: 1500,
          usedMinutes: 320,
          totalUsedMinutes: 320,
          freeNotes: 100,
          freeCharacters: 500000,
          totalMedia: 42,
          referralId: "",
          invoiceLink: "",
          notes: { totalNotes: 100, remainingNotes: 80 },
          cost: {
            insights: 0,
            prompts: 2,
            humanTranscription: 0,
            monthly: 15,
            texts: 0,
            total: 15,
            discountedTotal: 15,
            dubbing: 0,
            translation: 0,
          },
          features: {
            collaboration: {
              adminControl: true,
              allowUsers: 5,
              assignStatus: true,
              groupTeamMember: true,
              teamStatistics: true,
            },
            embed: {
              analytics: true,
              brandCustomization: true,
              globalBrandCustomization: false,
              isFooter: false,
              library: true,
              player: true,
              watermark: false,
              isPlayerDomain: false,
              isLibraryDomain: false,
            },
            developers: { apiKeys: true, webhooks: true },
            export: {
              bulk: true,
              premium: true,
              csv: true,
              html: true,
              json: true,
              pdf: true,
              redaction: false,
              vtt: true,
              word: true,
            },
            import: { bulk: true, csv: true, fileSizeInGB: 10 },
            insights: { edit: true, custom: true },
            texts: { csv: 100, notes: 100 },
            recorder: {
              brandCustomization: true,
              download: true,
              isFooter: false,
              isRecorderDomain: false,
            },
            support: { liveChat: true, phone: false, training: false },
            transcript: { edit: true, totalMinutes: 1500 },
            translation: { dubbingMinutes: 0, totalCharacters: 0 },
            prompt: {
              isEmbeddings: true,
              totalCharacters: 500000,
              folderChat: true,
              mediaChat: true,
            },
            whiteLabel: { hosting: false },
            meetingAssistant: { isActive: true, customization: true },
            delete: { isCustomize: false, mediaInDays: -1 },
            liveTranscription: { isEnabled: true, reprocessFile: true },
            liveTranslation: { isEnabled: false },
          },
        },
      }
    : null;

function getDevMock(): UserSubscription | null {
  if (!DEV_MOCK_SUBSCRIPTIONS || typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const mock = params.get("mock");
  return mock ? (DEV_MOCK_SUBSCRIPTIONS[mock] ?? null) : null;
}

/**
 * Fetch current user subscription details.
 *
 * Dev testing: add ?mock=pro | ?mock=payg | ?mock=cancel to simulate subscription states.
 */
export function useSubscription(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: subscriptionKeys.current(),
    queryFn: async () => {
      const devMock = getDevMock();
      if (devMock) return devMock;
      const res = await paymentApi.getUserSubscription();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch subscription");
      return res.data!;
    },
    enabled: options?.enabled,
  });
}

/**
 * Fetch file size usage/quota.
 */
export function useFileSizeUsage() {
  return useQuery({
    queryKey: subscriptionKeys.fileSizeUsage(),
    queryFn: async () => {
      const res = await paymentApi.getFileSizeUsage();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch file size usage");
      return res.data!;
    },
  });
}

/**
 * Fetch invoice list.
 */
export function useInvoices(
  paymentSource: PaymentSourceType = "stripe" as PaymentSourceType,
) {
  return useQuery({
    queryKey: subscriptionKeys.invoices(paymentSource),
    queryFn: async () => {
      const res = await paymentApi.getInvoices(paymentSource);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch invoices");
      return res.data!;
    },
  });
}

/**
 * Fetch upcoming invoice.
 */
export function useUpcomingInvoice(
  paymentSource: PaymentSourceType = "stripe" as PaymentSourceType,
) {
  return useQuery({
    queryKey: subscriptionKeys.upcomingInvoice(paymentSource),
    queryFn: async () => {
      const res = await paymentApi.getUpcomingInvoice(paymentSource);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch upcoming invoice");
      const raw = res.data;
      return raw ? [raw] : [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch transaction history.
 */
export function useTransactions(
  paymentSource: PaymentSourceType = "stripe" as PaymentSourceType,
) {
  return useQuery({
    queryKey: subscriptionKeys.transactions(paymentSource),
    queryFn: async () => {
      const res = await paymentApi.getTransactions(paymentSource);
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch transactions");
      return res.data!;
    },
  });
}

/**
 * Fetch Stripe subscription intent (for creating new subscriptions).
 */
export function useSubscriptionIntent(enabled = false) {
  return useQuery({
    queryKey: subscriptionKeys.intent(),
    queryFn: async () => {
      const res = await paymentApi.getSubscriptionIntent();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch subscription intent");
      return res.data!;
    },
    enabled,
  });
}

/**
 * Fetch Paddle transaction (for Paddle-based subscriptions).
 */
export function usePaddleTransaction(enabled = false) {
  return useQuery({
    queryKey: subscriptionKeys.paddleTransaction(),
    queryFn: async () => {
      const res = await paymentApi.getPaddleTransaction();
      if (!res.success)
        throw new Error(res.error ?? "Failed to fetch Paddle transaction");
      return res.data!;
    },
    enabled,
  });
}
